import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, TextField, Select, MenuItem, FormControl,
    InputLabel, Button, CircularProgress, Alert, Grid,
    Card, CardContent, Divider, Rating, Avatar, Tooltip, Radio,
    Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GroupsIcon from '@mui/icons-material/Groups';
import SearchIcon from '@mui/icons-material/Search';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import BadgeIcon from '@mui/icons-material/Badge';
import dayjs from 'dayjs';

const API_BASE_URL = process.env.REACT_APP_API_BASE || 'http://localhost:5000';

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1.5),
    backgroundColor: '#fdfdfd',
    border: '1px solid #e0e0e0',
}));

const ResultCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    border: '1px solid #e0e0e0',
    boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    transition: 'all 0.3s ease',
    '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
        borderColor: theme.palette.primary.main,
    },
}));

const UserRow = ({ user }) => {
    const [reviews, setReviews] = useState([]);
    const theme = useTheme();
    const [newReview, setNewReview] = useState({ rating: 0, comment: '' });
    const [dialog, setDialog] = useState({ open: false, title: '', message: '' });

    
    const fetchReviews = async () => {
        try {
            const token = localStorage.getItem('jwtToken');
            const response = await axios.get(`${API_BASE_URL}/api/reviews/${user.role}/${user.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReviews(response.data.reviews);
        } catch (error) {
            console.error('Failed to fetch reviews:', error);
        }
    };

 
    const handlePostReview = async () => {
        if (newReview.rating === 0) {
            setDialog({ open: true, title: 'Rating Required', message: 'Please select a star rating before submitting.' });
            return;
        }
        try {
            const token = localStorage.getItem('jwtToken');
            await axios.post(`${API_BASE_URL}/api/reviews/${user.role}/${user.id}`, newReview, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setDialog({ open: true, title: 'Review Submitted', message: 'Review submitted successfully and is pending approval.' });
            setNewReview({ rating: 0, comment: '' });
        } catch (error) {
            setDialog({ open: true, title: 'Error', message: 'Failed to post review. ' + (error.response?.data?.message || '') });
        }
    };

    useEffect(() => {
        fetchReviews();
    }, []);

    const reviewSummary = useMemo(() => {
        if (reviews.length === 0) return { avg: 0, count: 0 };
        const total = reviews.reduce((acc, r) => acc + r.rating, 0);
        return {
            avg: total / reviews.length,
            count: reviews.length
        };
    }, [reviews]);

    return (
        <ResultCard>
            <Dialog
                open={dialog.open}
                onClose={() => setDialog({ ...dialog, open: false })}
            >
                <DialogTitle>{dialog.title}</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {dialog.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialog({ ...dialog, open: false })} autoFocus>Close</Button>
                </DialogActions>
            </Dialog>

            <CardContent sx={{ pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {user.admin_verification_status === 'verified' ? (
                            <Tooltip title="Verified Company">
                                <VerifiedUserIcon sx={{ color: '#2196F3', fontSize: '1.5rem' }} />
                            </Tooltip>
                        ) : (
                            <Tooltip title="Unverified">
                                <VerifiedUserIcon sx={{ color: 'grey', fontSize: '1.5rem' }} />
                            </Tooltip>
                        )}
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main', lineHeight: 1.2 }}>
                                {user.company_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize', fontWeight: 500, mt: 0.25 }}>
                                {user.role}
                            </Typography>
                        </Box>
                    </Box>

                    {reviewSummary.count > 0 && (
                        <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 1,
                            bgcolor: '#f5f5f5',
                            px: 2,
                            py: 1,
                            borderRadius: 2,
                            border: '1px solid #e0e0e0'
                        }}>
                            <Rating value={reviewSummary.avg} precision={0.1} readOnly size="small" />
                            <Typography variant="body2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                {reviewSummary.avg.toFixed(1)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                ({reviewSummary.count} {reviewSummary.count === 1 ? 'review' : 'reviews'})
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>

            <Divider />

          
            <CardContent sx={{ bgcolor: '#fafafa' }}>
                <Grid container spacing={3}>
                   
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                            Company Information
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <LocationOnIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.2 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                        Location
                                    </Typography>
                                    <Typography variant="body2">
                                        {user.street ? `${user.street}, ${user.city_st_zip}` : user.city_st_zip || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <PhoneIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.2 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                        Contact
                                    </Typography>
                                    <Typography variant="body2">
                                        {user.phone_number || user.email || 'N/A'}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                <CalendarTodayIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.2 }} />
                                <Box>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                                        Member Since
                                    </Typography>
                                    <Typography variant="body2">
                                        {dayjs(user.created_at).format('MMMM DD, YYYY')}
                                    </Typography>
                                </Box>
                            </Box>
                            
                            {user.role !== 'shipper' && (
    <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <BadgeIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.2 }} />
            <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                    DOT Number
                </Typography>
                <Typography variant="body2">
                    {user.dot_number ? user.dot_number : 'N/A'}
                </Typography>
            </Box>
        </Box>
        
        {user.mc_number && user.mc_number !== 'MC-' && (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <BadgeIcon sx={{ fontSize: '1.2rem', color: 'text.secondary', mt: 0.2 }} />
                <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontWeight: 600 }}>
                        MC Number
                    </Typography>
                    <Typography variant="body2">
                        {user.mc_number}
                    </Typography>
                </Box>
            </Box>
        )}
    </>
)}
                        </Box>
                    </Grid>

                    
                    <Grid item xs={12} md={6}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}>
                            Share Your Experience
                        </Typography>
                        <Box sx={{ 
                            bgcolor: 'white', 
                            p: 2, 
                            borderRadius: 2,
                            border: '1px solid #e0e0e0'
                        }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, fontWeight: 600 }}>
                                Your Rating
                            </Typography>
                            <Rating
                                name="new-review-rating"
                                value={newReview.rating}
                                size="large"
                                onChange={(event, newValue) => {
                                    setNewReview(prev => ({ ...prev, rating: newValue }));
                                }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                fullWidth
                                multiline
                                rows={3}
                                label="Write your review"
                                placeholder="Share details about your experience with this company..."
                                value={newReview.comment}
                                onChange={(e) => setNewReview(prev => ({ ...prev, comment: e.target.value }))}
                                size="small"
                                sx={{ mb: 2 }}
                            />
                            <Button 
                                variant="contained" 
                                size="medium"
                                fullWidth
                                onClick={handlePostReview}
                                sx={{ textTransform: 'none', fontWeight: 600 }}
                            >
                                Submit Review
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>

            <Divider />

            <CardContent sx={{ bgcolor: 'white' }}>
                <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                         Reviews
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {reviewSummary.count === 0 
                            ? 'Be the first to review this company' 
                            : `Based on ${reviewSummary.count} ${reviewSummary.count === 1 ? 'review' : 'reviews'}`
                        }
                    </Typography>
                </Box>

                <Box sx={{ width: '100%' }}>
                    {reviews && reviews.length > 0 ? (
                        <Grid container spacing={2}>
                            {reviews.map((review) => (
                                <Grid item xs={12} key={review.id}>
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            p: 2.5,
                                            borderRadius: 2,
                                            border: '1px solid #e6e6e6',
                                            background: '#fafafa',
                                            transition: 'all 0.2s ease',
                                            '&:hover': {
                                                bgcolor: '#f5f5f5',
                                                borderColor: '#d0d0d0'
                                            }
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                            <Avatar 
                                                sx={{ 
                                                    width: 48, 
                                                    height: 48, 
                                                    bgcolor: 'primary.main',
                                                    fontSize: '1.2rem',
                                                    fontWeight: 700
                                                }}
                                            >
                                                {review.reviewer_company_name?.charAt(0)}
                                            </Avatar>
                                            
                                            <Box sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.2 }}>
                                                            {review.reviewer_company_name}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                                            {dayjs(review.created_at).format("MMMM DD, YYYY")}
                                                        </Typography>
                                                    </Box>
                                                    
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Rating value={review.rating} readOnly size="small" />
                                                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                            {review.rating}.0
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        background: 'white',
                                                        p: 2,
                                                        borderRadius: 1.5,
                                                        lineHeight: 1.6,
                                                        border: '1px solid #e6e6e6',
                                                        color: 'text.primary'
                                                    }}
                                                >
                                                    {review.comment}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Paper>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Box sx={{ 
                            textAlign: 'center', 
                            py: 6,
                            bgcolor: '#fafafa',
                            borderRadius: 2,
                            border: '1px dashed #d0d0d0'
                        }}>
                            <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                                No reviews yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                Be the first to share your experience
                            </Typography>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </ResultCard>
    );
};

const Directory = () => {
    const [filters, setFilters] = useState({
        type: 'carrier',
        name: '',
        regNumber: '',
    });
    const [idType, setIdType] = useState('DOT'); 
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
    
        if (name === 'type') {
            setFilters({ type: value, name: '', regNumber: '' });
           
            if (value === 'shipper') {
                setIdType('DOT');
            }
        } else {
            setFilters(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleIdTypeChange = (newType) => {
        setIdType(newType);
     
        setFilters(prev => ({ ...prev, regNumber: '' }));
    };

    const handleIdChange = (e) => {
        const { value } = e.target;
        let displayValue = value.toUpperCase();

        if (idType === 'MC') {
            if (!displayValue.startsWith('MC-')) {
                displayValue = 'MC-' + displayValue.replace(/^MC-?/, '');
            }
           
            const digits = displayValue.split('MC-')[1] || '';
            displayValue = `MC-${digits.replace(/\D/g, '')}`;
        } else {
          
            displayValue = displayValue.replace(/\D/g, '');
        }

        setFilters(prev => ({ ...prev, regNumber: displayValue }));
    };

    const handleSearch = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = localStorage.getItem("jwtToken");
            const response = await axios.get(`${API_BASE_URL}/api/directory/search`, {
                headers: { Authorization: `Bearer ${token}` },
                params: filters
            });

            setResults(response.data);
            
            setFilters(prev => ({ ...prev, name: '', regNumber: '' }));

        } catch (err) {
            setError(err.response?.data?.message || "Failed to fetch directory results.");
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };

    useEffect(() => {
      
    }, []);

    return (
        <Box sx={{ p: 3 }}>
            <StyledPaper>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Type</InputLabel>
                            <Select
                                name="type"
                                value={filters.type}
                                label="Type"
                                onChange={handleFilterChange}
                            >
                                
                                <MenuItem value="carrier">Carrier</MenuItem>
                                <MenuItem value="shipper">Shipper</MenuItem>
                                <MenuItem value="broker">Broker</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={5}>
                        <TextField
                            fullWidth
                            size="small"
                            label="Search by Company Name"
                            name="name"
                            value={filters.name}
                            onChange={handleFilterChange}
                            onKeyPress={handleKeyPress}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                            fullWidth
                            size="small"
                            label={idType === 'MC' ? 'MC Number' : 'DOT Number'}
                            name="regNumber"
                            value={filters.regNumber}
                            onChange={handleIdChange}
                            onKeyPress={handleKeyPress}
                            disabled={filters.type === 'shipper'}
                        />
                         <FormControlLabel
                            control={
                                <Radio
                                    checked={idType === 'MC'}
                                    onChange={() => handleIdTypeChange('MC')}
                                    size="small"
                                    disabled={filters.type === 'shipper'}
                                />
                            }
                            label="MC"
                        />
                        <FormControlLabel
                            control={
                                <Radio
                                    checked={idType === 'DOT'}
                                    onChange={() => handleIdTypeChange('DOT')}
                                    size="small"
                                    disabled={filters.type === 'shipper'}
                                />
                            }
                            label="DOT"
                        />
                    </Grid>
                    <Grid item xs={12} sm={1}>
                        <Button
                            fullWidth
                            variant="contained"
                            startIcon={<SearchIcon />}
                            onClick={handleSearch}
                            disabled={loading || (filters.name.trim() === "" && filters.regNumber.trim() === "")}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 'bold'
                            }}
                        >
                            Search
                        </Button>
                    </Grid>
                </Grid>
            </StyledPaper>

            {loading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                    <CircularProgress />
                </Box>
            )}

            {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}

            {!loading && (
                <Box>
                    {results.length > 0 ? (
                        results.map(user => <UserRow key={`${user.role}-${user.id}`} user={user} />)
                    ) : (
                        <Box sx={{ textAlign: 'center', mt: 5 }}>
                            <GroupsIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                            <Typography color="text.secondary" align="center" sx={{ mt: 2 }}>
                                No results found. Try adjusting your filters.
                            </Typography>
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default Directory;